import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const AusMeinesHerzensGrundeViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Aus meines Herzens Grunde
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV269/bwv_269/bwv_269-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Aus meines Herzens Grunde.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-3207">
      <Navbar data-id="element-3208" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-3209">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-3210">
          <img src="https://images.unsplash.com/photo-1519683109079-d5f539e1542f?w=400&h=300&fit=crop" alt="Aus meines Herzens Grunde" className="w-full h-64 md:h-80 object-cover" data-id="element-3211" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-3212" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-3213">
            <div className="max-w-7xl mx-auto" data-id="element-3214">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-3215">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-3216" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-3217">
                Aus meines Herzens Grunde
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-3218">
                BWV 269, Chorale
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-3219">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-3220">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-3221">
          <div className="flex items-center space-x-2" data-id="element-3222">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-3223">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-3224" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3225">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-3226">
                  <ZoomInIcon className="h-5 w-5" data-id="element-3227" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-3228">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-3229">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-3230" />
              <span className="hidden sm:inline" data-id="element-3231">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-3232">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-3233" />
              <span className="hidden sm:inline" data-id="element-3234">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-3235">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-3236">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-3237">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Aus meines Herzens Grunde Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-3238" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-3239">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3240">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-3241">
            <div data-id="element-3242">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3243">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-3244">
                <div data-id="element-3245">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3246">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3247">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-3248">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3249">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3250">
                    18th Century
                  </dd>
                </div>
                <div data-id="element-3251">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3252">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3253">
                    Chorale
                  </dd>
                </div>
                <div data-id="element-3254">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3255">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3256">
                    BWV 269
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-3257">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3258">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3259">
                This four-part chorale exemplifies Bach's mastery of harmonic
                writing and voice leading. The piece features the characteristic
                hymn-like texture of Lutheran chorales, with each voice moving
                independently yet creating a cohesive harmonic structure.
                Perfect for developing sight-reading skills and understanding
                Baroque harmonic progressions, this chorale demonstrates Bach's
                ability to create profound musical expression within a simple,
                devotional framework.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-3260" />
    </div>;
};